import { CSSProperties, FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PricingOnRequest, RiskIndicator, SvgIcon } from '@/components';
import { AmlFlowEnum } from '@/api';
import { PlanCategoryEnum, tariffDisplayData } from '@/config';

import styles from './TariffsProPlusDetails.module.sass';

const ACCENT_COLOR = tariffDisplayData[PlanCategoryEnum.PRO_PLUS].color;

const FEATURE_ICONS = [
  'risk',
  'connect',
  'dashboard',
  'compliance-dashboard',
  'monitoring',
  'scan-verify',
  'investigations',
  'question-circle',
];

interface TariffsProPlusDetailsProps {}

const TariffsProPlusDetails: FC<TariffsProPlusDetailsProps> = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: FEATURE_ICONS[0],
      title: t('translation.tariffs_pro_plus_feature_risk_title'),
      text: t('translation.tariffs_pro_plus_feature_risk_text'),
    },
    {
      icon: FEATURE_ICONS[1],
      title: t('translation.tariffs_pro_plus_feature_connected_title'),
      text: t('translation.tariffs_pro_plus_feature_connected_text'),
    },
    {
      icon: FEATURE_ICONS[2],
      title: t('translation.tariffs_pro_plus_feature_sources_title'),
      text: t('translation.tariffs_pro_plus_feature_sources_text'),
    },
    {
      icon: FEATURE_ICONS[3],
      title: t('translation.tariffs_pro_plus_feature_counterparty_title'),
      text: t('translation.tariffs_pro_plus_feature_counterparty_text'),
    },
    {
      icon: FEATURE_ICONS[4],
      title: t('translation.tariffs_pro_plus_feature_monitoring_title'),
      text: t('translation.tariffs_pro_plus_feature_monitoring_text'),
    },
    {
      icon: FEATURE_ICONS[5],
      title: t('translation.tariffs_pro_plus_feature_kyt_title'),
      text: t('translation.tariffs_pro_plus_feature_kyt_text'),
    },
    {
      icon: FEATURE_ICONS[6],
      title: t('translation.tariffs_pro_plus_feature_investigations_title'),
      text: t('translation.tariffs_pro_plus_feature_investigations_text'),
    },
    {
      icon: FEATURE_ICONS[7],
      title: t('translation.tariffs_pro_plus_feature_support_title'),
      text: t('translation.tariffs_pro_plus_feature_support_text'),
    },
  ];

  return (
    <div
      className={styles.details}
      style={{ '--pro-plus-accent': ACCENT_COLOR } as CSSProperties}
    >
      <div className={styles.main}>
        <div className={styles.header}>
          <div className={styles.eyebrow}>
            {t('translation.tariffs_pro_plus_eyebrow')}
          </div>
          <div className={styles.title}>
            {t('translation.tariffs_pro_plus_title')}
          </div>
          <div className={styles.subtitle}>
            {t('translation.tariffs_pro_plus_subtitle')}
          </div>
        </div>

        <div className={styles.indicator}>
          <RiskIndicator value={10} amlFlow={AmlFlowEnum.ACCURATE} />
          <div className={styles.indicatorLabel}>
            {t('translation.tariffs_pro_plus_indicator_label')}
          </div>
        </div>

        <div className={styles.verifiedBanner}>
          <div className={styles.verifiedGlow} aria-hidden="true" />
          <div className={styles.verifiedIcon}>
            <SvgIcon name="verified" className={styles.verifiedIconSvg} />
          </div>
          <div className={styles.verifiedBody}>
            <div className={styles.verifiedBadge}>
              {t('translation.tariffs_pro_plus_verified_badge')}
            </div>
            <div className={styles.verifiedTitle}>
              {t('translation.tariffs_pro_plus_verified_title')}
            </div>
            <div className={styles.verifiedText}>
              {t('translation.tariffs_pro_plus_verified_text')}
            </div>
          </div>
        </div>

        <div className={styles.features}>
          {features.map((feature) => (
            <div key={feature.title} className={styles.feature}>
              <div className={styles.featureIcon}>
                <SvgIcon name={feature.icon} className={styles.featureSvg} />
              </div>
              <div className={styles.featureBody}>
                <div className={styles.featureTitle}>{feature.title}</div>
                <div className={styles.featureText}>{feature.text}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <PricingOnRequest color={ACCENT_COLOR} className={styles.pricing} />
    </div>
  );
};

export default TariffsProPlusDetails;
